import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Upload, X } from 'lucide-react';
import { inventoryService } from '../../services/inventoryService';
import type { InventoryLot, StockMovement, InventoryItem } from '../../types/inventory';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { formatCurrency } from '../../utils/format';
import { useInventoryImage } from '../../hooks/useInventoryImage';
import { Modal } from '../../components/common/Modal';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../utils/cn';
import { greenBeanService } from '../../services/greenBeanService';
import { DownloadLabelButton } from '../../components/labels/DownloadLabelButton';
import { LotLabelDocument } from '../../components/labels/LotLabelDocument';
import { lotScanUrl } from '../../utils/labelUrl';
import { ProfileManager } from '../../components/inventory/ProfileManager';
import { LotRoastWorkOrderCell } from '../purchasing/components/LotRoastWorkOrderCell';
import { workOrderService } from '../../services/workOrderService';

export const LotDetailPage: React.FC = () => {
  const { t } = useLanguage();
  const { can } = usePermissions();
  const canWriteInventory = can('inventory', 'write');
  const readOnly = !canWriteInventory;
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [lot, setLot] = useState<InventoryLot | null>(null);
  const [item, setItem] = useState<InventoryItem | null>(null);
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const [lotData, historyData] = await Promise.all([
          inventoryService.getLotById(id),
          inventoryService.getLotHistory(id)
        ]);
        const itemData = await inventoryService.getById(lotData.inventoryItemId);
        setLot(lotData);
        setMovements(historyData.data);
        setItem(itemData);
      } catch (error) {
        console.error('Failed to fetch lot details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const status = lot?.remainingQuantity === 0 ? 'Depleted' : 'Active';
  const statusColor = status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';

  const isGreenBean = item?.itemType === 'green_bean';
  const { data: imageUrl, isLoading: isImageLoading } = useInventoryImage(isGreenBean ? lot?.id : undefined);
  const { data: greenBeanProfile } = useQuery({
    queryKey: ['lotGreenBeanProfile', lot?.id],
    queryFn: () => greenBeanService.getByLot(lot!.id),
    enabled: !!lot?.id,
  });
  // Completed work orders that consumed this lot back the roast-profile action,
  // mirroring the purchasing Lot List. Only green-bean lots surface the profiles.
  const { data: completedWoByLot, isLoading: isWoLoading } = useQuery({
    queryKey: ['work-orders-completed-by-lots', lot?.id],
    queryFn: () => workOrderService.getCompletedByLots([lot!.id]),
    enabled: !!lot?.id && isGreenBean,
  });
  const refreshProfiles = () => {
    queryClient.invalidateQueries({ queryKey: ['lotGreenBeanProfile', lot?.id] });
  };
  const [uploadError, setUploadError] = useState('');

  const uploadMutation = useMutation({
    mutationFn: (file: File) => inventoryService.uploadImage(lot!.id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventoryImage', lot?.id] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'lot', lot?.id] });
      setLot((prev) => (prev ? { ...prev, photoUploadedAt: new Date().toISOString() } : prev));
      setUploadError('');
    },
    onError: (error: unknown) => {
      if (isAxiosError(error)) {
        setUploadError((error.response?.data as { error?: string } | undefined)?.error || t('inventory.lotDetail.errors.uploadFailed'));
        return;
      }
      setUploadError(t('inventory.lotDetail.errors.uploadFailed'));
    }
  });

  const deleteMutation = useMutation({
    mutationFn: () => inventoryService.deleteImage(lot!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventoryImage', lot?.id] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'lot', lot?.id] });
      setLot((prev) => (prev ? { ...prev, photoUploadedAt: undefined, photoName: undefined, photoUrl: undefined } : prev));
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  });

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setUploadError(t('inventory.lotDetail.errors.fileTooLarge'));
      return;
    }

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError(t('inventory.lotDetail.errors.invalidFileType'));
      return;
    }

    uploadMutation.mutate(file);
  };

  if (loading) return <LoadingSpinner text={t('inventory.lotDetail.loading')} />;
  if (!lot) return <div className="p-8 text-center text-red-500">{t('inventory.lotDetail.notFound')}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button 
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <ArrowLeft className="h-5 w-5 text-gray-500" />
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">{t('inventory.lotDetail.titleWithNumber', { lotNumber: lot.lotNumber })}</h1>
        <span className={`px-3 py-1 text-sm font-semibold rounded-full ${statusColor}`}>
          {status === 'Active' ? t('inventory.lotDetail.status.active') : t('inventory.lotDetail.status.depleted')}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 mb-4">{t('inventory.lotDetail.sections.lotDetails')}</h2>
          <dl className="space-y-4">
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">{t('inventory.lotDetail.fields.item')}</dt>
              <dd className="text-sm font-medium text-gray-900 text-right max-w-[60%] break-words">
                <div className="font-medium">{lot.itemName || t('inventory.lotDetail.values.unknownItem')}</div>
                <div className="text-xs text-gray-500 text-right">{lot.itemCode || lot.inventoryItemId}</div>
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">{t('inventory.lotDetail.fields.receivedDate')}</dt>
              <dd className="text-sm font-medium text-gray-900">{new Date(lot.receivedDate).toLocaleDateString()}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">{t('inventory.lotDetail.fields.expiryDate')}</dt>
              <dd className="text-sm font-medium text-gray-900">{lot.expiryDate ? new Date(lot.expiryDate).toLocaleDateString() : t('inventory.lotDetail.values.na')}</dd>
            </div>
             <div className="flex justify-between">
              <dt className="text-sm text-gray-500">{t('inventory.lotDetail.fields.source')}</dt>
              <dd className="text-sm font-medium text-gray-900 text-right">
                {lot.sourceType === 'purchase_order' && lot.sourceId ? (
                   <Link to={`/purchase-orders`} className="text-primary-600 hover:underline">
                      {lot.sourceRef || lot.sourceId}
                   </Link>
                ) : (
                  <span>{lot.sourceRef || lot.sourceId || t('inventory.lotDetail.values.na')}</span>
                )}
                <div className="text-xs text-gray-400 capitalize">{lot.sourceType?.replace('_', ' ')}</div>
              </dd>
            </div>
            {lot.sampleId && (
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">{t('inventory.lotDetail.fields.linkedSample')}</dt>
                <dd className="text-sm font-medium text-gray-900 text-right">
                  <Link 
                    to={`/purchasing/samples?search=${lot.sampleId}`}
                    className="text-indigo-600 hover:underline"
                  >
                    {t('inventory.lotDetail.actions.viewSample')}
                  </Link>
                </dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">{t('inventory.lotDetail.fields.supplier')}</dt>
              <dd className="text-sm font-medium text-gray-900 text-right flex flex-row items-center space-x-2">
                <div>{lot.supplierName || lot.supplierId || t('inventory.lotDetail.values.na')}</div>
                {(lot.supplierEmail || lot.supplierPhone) && (
                  <div className="mt-1 space-x-1 text-xs text-gray-500">
                    {lot.supplierEmail && (
                      <a
                        href={`mailto:${lot.supplierEmail}`}
                        className="text-primary-600 hover:text-primary-900 inline-flex items-center gap-1 hover:underline"
                      >
                        <Mail className="h-5 w-5" />
                      </a>
                    )}
                    {lot.supplierPhone && (
                      <a
                        href={`tel:${lot.supplierPhone}`}
                        className="text-green-600 hover:text-primary-900 inline-flex items-center gap-1 hover:underline"
                      >
                        <Phone className="h-5 w-5" />
                      </a>
                    )}
                  </div>
                )}
              </dd>
            </div>
             {lot.unitPrice !== undefined && (
              <div className="flex justify-between">
              <dt className="text-sm text-gray-500">{t('inventory.lotDetail.fields.unitPrice')}</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {formatCurrency(lot.unitPrice, 'Rp')}
                </dd>
              </div>
            )}
            <div className="pt-2">
              <DownloadLabelButton
                scanUrl={lotScanUrl(lot.id)}
                renderDocument={(qr) => (
                  <LotLabelDocument lot={lot} profile={greenBeanProfile ?? null} qrDataUrl={qr} />
                )}
                fileName={`lot-label-${lot.lotNumber}`}
                label={t('labels.downloadLabel')}
                data-test-id="lot-download-label"
              />
            </div>
          </dl>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex flex-col">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-lg font-medium text-gray-900">{t('inventory.lotDetail.sections.inventoryStatus')}</h2>
            {isGreenBean && !readOnly && (
              <input
                type="file"
                ref={fileInputRef}
                accept="image/png, image/jpeg, image/jpg, image/webp"
                className="hidden"
                onChange={handleImageUpload}
              />
            )}
          </div>
          
          <dl className="space-y-4 mb-6">
             <div className="flex justify-between">
              <dt className="text-sm text-gray-500">{t('inventory.lotDetail.fields.initialQuantity')}</dt>
              <dd className="text-sm font-medium text-gray-900">{lot.initialQuantity}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">{t('inventory.lotDetail.fields.remainingQuantity')}</dt>
              <dd className="text-sm font-medium text-gray-900">{lot.remainingQuantity}</dd>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
              <div 
                className="bg-primary-600 h-2.5 rounded-full" 
                style={{ width: `${Math.min((lot.remainingQuantity / lot.initialQuantity) * 100, 100)}%` }}
              ></div>
            </div>
          </dl>

          {isGreenBean && (
            <div className="mt-auto border-t border-gray-100 pt-6">
              <div className="mb-3 flex items-center justify-between gap-4">
                <label className="block text-sm font-medium text-gray-700">{t('inventory.lotDetail.fields.greenBeanPhoto')}</label>
                {lot.photoUploadedAt && (
                  <span className="text-xs text-gray-500">
                    {t('inventory.lotDetail.fields.photoUploadedAt')}: {new Date(lot.photoUploadedAt).toLocaleString()}
                  </span>
                )}
              </div>
              <div 
                className={cn(
                  "relative group flex items-center justify-center bg-gray-50 border border-gray-200 border-dashed rounded-lg w-full min-h-[12rem] p-4 transition-colors",
                  (!readOnly || imageUrl) && "cursor-pointer hover:bg-gray-100"
                )}
                onClick={() => {
                  if (imageUrl) {
                    setIsPhotoModalOpen(true);
                  } else if (!readOnly) {
                    fileInputRef.current?.click();
                  }
                }}
              >
                {isImageLoading || uploadMutation.isPending ? (
                  <div className="flex flex-col items-center gap-2">
                    <LoadingSpinner size="sm" />
                    <span className="text-xs text-gray-500 font-medium">{uploadMutation.isPending ? t('inventory.lotDetail.uploading') : t('inventory.lotDetail.loadingShort')}</span>
                  </div>
                ) : imageUrl ? (
                  <>
                    <img
                      src={imageUrl}
                      alt={t('common.terms.greenBean')}
                      className="max-h-40 w-full object-contain group-hover:opacity-75 transition-opacity"
                    />
                    {!readOnly && (
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/10 rounded-lg">
                        <div 
                          className="bg-white text-gray-700 px-4 py-2 rounded-full text-xs font-medium shadow-md flex items-center gap-2 hover:bg-gray-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            fileInputRef.current?.click();
                          }}
                        >
                          <Upload className="h-4 w-4" /> {t('inventory.lotDetail.actions.changePhoto')}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className={cn(
                    "flex flex-col items-center gap-3 text-gray-400 transition-colors py-4",
                    !readOnly && "group-hover:text-primary-500"
                  )}>
                    <Upload className="h-10 w-10 stroke-1" />
                    <div className="text-center">
                      <p className="text-sm font-medium">{readOnly ? t('settingsMcp.values.none') : t('inventory.lotDetail.actions.clickUpload')}</p>
                      {!readOnly && <p className="text-xs mt-1">{t('inventory.lotDetail.uploadHint')}</p>}
                    </div>
                  </div>
                )}
                
                {imageUrl && !isImageLoading && !uploadMutation.isPending && !readOnly && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteMutation.mutate();
                    }}
                    className="absolute -top-3 -right-3 bg-white text-red-500 hover:bg-red-500 hover:text-white rounded-full p-2 shadow-lg transition-all border border-red-100 z-10"
                    title={t('inventory.lotDetail.actions.removePhoto')}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {uploadError && <p className="text-xs text-red-600 mt-2 text-center">{uploadError}</p>}
            </div>
          )}
        </div>
      </div>

      {isGreenBean && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 mb-4">{t('inventory.lotDetail.sections.profiles')}</h2>
          <div className="grid grid-cols-1 divide-y divide-gray-100 sm:grid-cols-3 sm:divide-y-0 sm:divide-x">
            <div className="flex flex-col items-center gap-3 px-4 py-4 sm:py-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider text-center">{t('purchasing.lotList.table.greenBeanProfile')}</span>
              <ProfileManager
                entityType="LotType"
                entityId={lot.id}
                profileType="greenBean"
                existingProfile={greenBeanProfile ?? undefined}
                onProfileUpdate={refreshProfiles}
              />
            </div>
            <div className="flex flex-col items-center gap-3 px-4 py-4 sm:py-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider text-center">{t('purchasing.lotList.table.roastProfile')}</span>
              <LotRoastWorkOrderCell
                lotId={lot.id}
                workOrders={completedWoByLot?.[lot.id] ?? []}
                readOnly={readOnly}
                isLoading={isWoLoading}
              />
            </div>
            <div className="flex flex-col items-center gap-3 px-4 py-4 sm:py-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider text-center">{t('purchasing.lotList.table.tasteProfile')}</span>
              <ProfileManager
                entityType="LotType"
                entityId={lot.id}
                profileType="cupping"
                onProfileUpdate={refreshProfiles}
              />
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">{t('inventory.lotDetail.sections.stockMovementHistory')}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.general.table.date')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.shift.type')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('inventory.lotDetail.fields.quantityChange')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.general.table.reference')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('inventory.lotDetail.fields.user')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {movements.map((movement) => {
                 const effectiveQty =
                  movement.movementType === 'issue'
                    ? -Math.abs(movement.quantity)
                    : movement.quantity;

                const isPositive = effectiveQty >= 0;
                return (
                <tr key={movement.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(movement.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                    {movement.movementType.replace('_', ' ')}
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium
                      ${isPositive ? 'text-green-600' : 'text-red-600'}
                    `}
                  >
                    {isPositive ? '+' : '-'}{Math.abs(effectiveQty)}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {movement.referenceType}: {movement.referenceId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {movement.userName}
                  </td>
                </tr>
              )})}
              {movements.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">{t('inventory.lotDetail.emptyMovements')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={isPhotoModalOpen}
        onClose={() => setIsPhotoModalOpen(false)}
        title={`${lot.itemName} - ${t('inventory.lotDetail.fields.photo')}`}
        maxWidth='4xl'
      >
        <div className="flex items-center justify-center p-4">
          {imageUrl && (
            <img src={imageUrl} alt={t('common.terms.greenBean')} className="max-w-full max-h-[70vh] rounded-lg shadow-md" />
          )}
        </div>
      </Modal>
    </div>
  );
};
